// ============================================
// TASK-9653 S1 — "open DispatchDeck at this screen" links:
//   #/hr?dd_open=hr_document_upload&folder={hrResourceId}
// TASK-9671 S1 — Claude lookup openAt: #/dispatch?dd_open=job&job={jobId},
//   #/maintenance?dd_open=vehicle&vehicle={truckOrTrailerId},
//   #/dispatch?dd_open=dispatch_board&date=YYYY-MM-DD (a bare #/{view}
//   landing carries no hint and is not in this table).
// TASK-9671 S2 — #/billing?dd_open=ticket&ticket={ticketId},
//   #/crm?dd_open=customer&customer={customerId}.
// TASK-9671 S3 — #/billing?dd_open=invoice&ticket={ticketIdOnTheInvoice},
//   #/crm?dd_open=price_book&customer={customerId}&contract={contractId}.
// ============================================
// Side-effect module (UMD): attaches `window.OpenLinks`. app.js imports it
// ONCE, before any view module; app.js (router) and the views that drain a
// hint (hr.js) read it through `window.OpenLinks` at CALL time — never a
// cross-`?v=` ES import (Pitfall #1). Node unit tests require() it:
// `node tests/task9653-open-in-dispatchdeck.unit.cjs`.
//
// THE GENERIC FOCUS QUEUE (TASK-9671 reuses it for record opens):
//   AppState.pendingFocus = { view, kind, ids, at, tenant }
//   1. The router parks a hint from the hash QUERY (parseOpenHint) — at module
//      load from the ORIGINAL hash (before any navigation rewrites it; a
//      doNavigateToView sets the hash to the bare `#/view`) and again on every
//      hashchange that carries one. Only enum kinds from DESTINATIONS, only
//      plain document ids.
//   2. Navigation to a DIFFERENT view, a no-access screen or a tenant change
//      drops it; it also expires after FOCUS_TTL_MS.
//   3. The view drains it with takeHint(view) once ITS data has loaded (HR:
//      the first hrResources snapshot), re-checks access, and opens the screen.
//      takeHint consumes the hint either way, so it opens at most once.
//
// IDS ONLY. A hint names an enum destination and opaque document ids; the view
// reads the record from its own live data and shows the NAME from that data —
// never text from the URL. The server builder is functions/src/utils/openLinks.js;
// the drift guard in tests/task9653-open-in-dispatchdeck.unit.cjs pins the two
// DESTINATIONS tables together.
(function (root, factory) {
  const api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root && typeof root === 'object') root.OpenLinks = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  const HINT_PARAM = 'dd_open';

  // destination → { view, params }. Keep in lockstep with the server twin.
  const VEHICLE_FILE = Object.freeze({ view: 'roster', params: Object.freeze(['vehicle']) }); // TASK-9717
  const DESTINATIONS = Object.freeze({
    hr_document_upload: Object.freeze({ view: 'hr', params: Object.freeze(['folder']) }),
    // TASK-9671 S1 — Claude lookup openAt (dispatch.js / maintenance.js drain).
    job: Object.freeze({ view: 'dispatch', params: Object.freeze(['job']) }),
    vehicle: Object.freeze({ view: 'maintenance', params: Object.freeze(['vehicle']) }),
    dispatch_board: Object.freeze({ view: 'dispatch', params: Object.freeze(['date']) }),
    // TASK-9671 S2 — billing.js / crm.js drain → lazy views/openLinkRecordDrain.js.
    ticket: Object.freeze({ view: 'billing', params: Object.freeze(['ticket']) }),
    customer: Object.freeze({ view: 'crm', params: Object.freeze(['customer']) }),
    // TASK-9671 S3 — an invoice opens one of its tickets (billing.js drain,
    // read view + View Invoice); a price book opens in CRM (crm.js drain).
    invoice: Object.freeze({ view: 'billing', params: Object.freeze(['ticket']) }),
    price_book: Object.freeze({ view: 'crm', params: Object.freeze(['customer', 'contract']) }),
    // TASK-9717 — file handoffs (roster.js drain → lazy views/rosterOpenLink.js).
    vehicle_registration: VEHICLE_FILE,
    vehicle_insurance: VEHICLE_FILE,
    vehicle_photo: VEHICLE_FILE,
    roster_photo_intake: Object.freeze({ view: 'roster', params: Object.freeze([]) }),
  });

  // Same window as RecordLinks: covers a cold load that stops for a sign-in,
  // short enough that a link tapped this morning can't pop a screen tonight.
  const FOCUS_TTL_MS = 10 * 60 * 1000;

  // What the server emits (functions/src/utils/openLinks.js OPAQUE_ID_RE).
  const OPAQUE_ID_RE = /^[A-Za-z0-9_-]{1,128}$/;
  function isOpaqueId(v) {
    return typeof v === 'string' && OPAQUE_ID_RE.test(v) && !/^__.*__$/.test(v);
  }
  // TASK-9671 S1 — a `date` param is a real YYYY-MM-DD day (server: isDateKey).
  function isDateKey(v) {
    if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
    const d = new Date(v + 'T12:00:00Z');
    return !isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
  }
  function _ok(k, v) { return k === 'date' ? isDateKey(v) : isOpaqueId(v); }

  /**
   * Parse a location hash into an open hint: `{ view, kind, ids }` or null.
   * The view is the first path segment; the hint is the `?query`. A kind not
   * in DESTINATIONS, a view that is not the kind's view, an unknown param, a
   * repeated param or a value that is not a plain document id → null.
   */
  function parseOpenHint(hash) {
    if (typeof hash !== 'string') return null;
    let h = hash;
    if (h.charAt(0) === '#') h = h.slice(1);
    if (h.charAt(0) === '/') h = h.slice(1);
    const q = h.indexOf('?');
    if (q === -1) return null;
    const view = h.slice(0, q).split('/')[0];
    const pairs = h.slice(q + 1).split('&').filter(Boolean);
    const got = Object.create(null);
    for (const pair of pairs) {
      const eq = pair.indexOf('=');
      if (eq <= 0) return null;
      let k; let v;
      try {
        k = decodeURIComponent(pair.slice(0, eq));
        v = decodeURIComponent(pair.slice(eq + 1));
      } catch (_) {
        return null; // malformed %-encoding
      }
      if (Object.prototype.hasOwnProperty.call(got, k)) return null;
      got[k] = v;
    }
    const kind = got[HINT_PARAM];
    if (typeof kind !== 'string' || !Object.prototype.hasOwnProperty.call(DESTINATIONS, kind)) return null;
    const d = DESTINATIONS[kind];
    if (view !== d.view) return null;
    const ids = {};
    for (const k of Object.keys(got)) {
      if (k === HINT_PARAM) continue;
      if (d.params.indexOf(k) === -1) return null;
      if (!_ok(k, got[k])) return null;
      ids[k] = got[k];
    }
    return { view, kind, ids };
  }

  function _as(appState) {
    return appState || (typeof window !== 'undefined' ? window.AppState : null);
  }

  /** Park a parsed hint (newest wins). Returns true when parked. */
  function parkHint(hint, appState, now) {
    const as = _as(appState);
    if (!as || !hint || !Object.prototype.hasOwnProperty.call(DESTINATIONS, hint.kind)) return false;
    const t = typeof now === 'number' ? now : Date.now();
    as.pendingFocus = { view: hint.view, kind: hint.kind, ids: Object.assign({}, hint.ids), at: t, tenant: as.tenant || null };
    return true;
  }

  /** Parse `hash` and park it when it carries a hint. Returns the hint or null. */
  function parkFromHash(hash, appState, now) {
    const hint = parseOpenHint(hash);
    if (hint) parkHint(hint, appState, now);
    return hint;
  }

  function _fresh(f, as, t) {
    if (!f || typeof f.at !== 'number') return false;
    if (t - f.at > FOCUS_TTL_MS || t < f.at - 60000) return false;
    // Parked under one company, drained under another = not this link's company.
    if (f.tenant && as.tenant && f.tenant !== as.tenant) return false;
    return true;
  }

  /**
   * Read-and-consume the parked hint for `view`: `{ kind, ids }` or null. A
   * hint for this view is cleared whether fresh or stale; another view's is
   * left alone.
   */
  function takeHint(view, appState, now) {
    const as = _as(appState);
    if (!as) return null;
    const f = as.pendingFocus;
    if (!f || f.view !== view) return null;
    as.pendingFocus = null;
    const t = typeof now === 'number' ? now : Date.now();
    if (!_fresh(f, as, t)) return null;
    if (!Object.prototype.hasOwnProperty.call(DESTINATIONS, f.kind)) return null;
    const ids = {};
    for (const k of Object.keys(f.ids || {})) {
      if (DESTINATIONS[f.kind].params.indexOf(k) === -1 || !_ok(k, f.ids[k])) return null;
      ids[k] = f.ids[k];
    }
    return { kind: f.kind, ids };
  }

  return {
    HINT_PARAM,
    DESTINATIONS,
    FOCUS_TTL_MS,
    isOpaqueId,
    isDateKey,
    parseOpenHint,
    parkHint,
    parkFromHash,
    takeHint,
  };
});
